import { useRequiredContext } from "@redotech/react-util/context";
import { useAsyncThrottle } from "@redotech/react-util/debounce";
import { useHandler } from "@redotech/react-util/hook";
import { useWaitForPrevious } from "@redotech/react-util/wait-for-previous";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getConversationsCounts } from "@redotech/redo-merchant-app-common/client/team";
import { MerchantAppTopic } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server";
import { MerchantAppEventServerContext } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useStreamingEnabled } from "@redotech/redo-merchant-fulfillment-app/common/streaming-enabled-context";
import {
  ConversationFiltersV3,
  FiltersStatus,
} from "@redotech/redo-model/conversation-filters/conversation-filters";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { FrontendTeamNoticeEvent } from "@redotech/redo-model/team-notifications/team-notifications";
import { sinkPromise } from "@redotech/util/promise";
import {
  ReactNode,
  createContext,
  memo,
  useContext,
  useEffect,
  useState,
} from "react";
import { maybeRunConversationsRefresh } from "../handle-notice-event";
import { ActiveViewContext } from "./active-view-context";
import { FinalizedFiltersContext } from "./filters-context";

export const ActiveViewConversationCountsContext = createContext<
  Record<string, number> | undefined
>({});

export const ActiveViewConversationCountsProvider = memo(
  function ConversationCountsProvider({
    children,
    blockCountRefreshes,
  }: {
    blockCountRefreshes: boolean;
    children: ReactNode | ReactNode[];
  }) {
    const client = useRequiredContext(RedoMerchantClientContext);
    const eventServer = useRequiredContext(MerchantAppEventServerContext);
    const activeFilters = useContext(FinalizedFiltersContext);
    const activeView = useContext(ActiveViewContext);
    const activeViewName = activeView.name;
    const team = useRequiredContext(TeamContext);
    const streamingEnabled = useStreamingEnabled();

    const hasSupportPermission = useHasPermission(
      SupportPermissions.VIEW_SUPPORT,
    );

    const supportEnabled = team?.settings.support?.enabled;
    const shouldLoadConversationCounts =
      streamingEnabled && supportEnabled && hasSupportPermission;

    useEffect(() => {
      if (!shouldLoadConversationCounts) {
        return;
      }

      const unlistenCallback = eventServer.subscribe({
        topic: MerchantAppTopic.TEAM,
        callback: async (message: FrontendTeamNoticeEvent) => {
          if (blockCountRefreshes) {
            return;
          }

          maybeRunConversationsRefresh(
            message,
            fetchConversationCountsIgnoreResult,
          );
        },
      });
      return () => unlistenCallback();
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blockCountRefreshes, shouldLoadConversationCounts, streamingEnabled]);

    useEffect(() => {
      if (!streamingEnabled) {
        return;
      }
      sinkPromise(fetchConversationCounts());
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters, streamingEnabled]);

    const [conversationCounts, setConversationCounts] = useState<
      Record<string, number>
    >({});

    const fetchConversationCounts = useHandler(async () => {
      if (!shouldLoadConversationCounts) {
        return;
      }

      const closedFilter = { ...activeFilters, status: FiltersStatus.CLOSED };
      const openFilter = { ...activeFilters, status: FiltersStatus.OPEN };
      const snoozedFilter = { ...activeFilters, status: FiltersStatus.SNOOZED };
      const inProgressFilter = {
        ...activeFilters,
        status: FiltersStatus.IN_PROGRESS,
      };

      const currentViewFilters: {
        name: string;
        filters: ConversationFiltersV3;
      }[] = [
        { name: `${activeViewName}-closed`, filters: closedFilter },
        { name: `${activeViewName}-open`, filters: openFilter },
        { name: `${activeViewName}-snoozed`, filters: snoozedFilter },
        ...(team.settings.support?.useInProgressStatus
          ? [
              {
                name: `${activeViewName}-in_progress`,
                filters: inProgressFilter,
              },
            ]
          : []),
      ];

      const rawConversationCounts = await getConversationsCounts(client, {
        views: currentViewFilters,
      });

      const counts = {
        ...rawConversationCounts,
        [`${activeViewName}-all`]: Object.values(rawConversationCounts).reduce(
          (acc, val) => acc + val,
          0,
        ),
      };

      setConversationCounts(counts);
    });

    const throttledFetchConversationCounts = useWaitForPrevious(
      fetchConversationCounts,
    );

    const throttledFetchFromNotifications = useAsyncThrottle(
      throttledFetchConversationCounts,
      Temporal.Duration.from({ seconds: 2 }),
    );

    const fetchConversationCountsIgnoreResult = useHandler(() => {
      sinkPromise(throttledFetchFromNotifications());
    });

    return (
      <ActiveViewConversationCountsContext.Provider value={conversationCounts}>
        {children}
      </ActiveViewConversationCountsContext.Provider>
    );
  },
);
